import { useEffect, useMemo, useState } from 'react';
import {
  collection, onSnapshot, query, orderBy, doc, updateDoc, setDoc, addDoc,
} from 'firebase/firestore';
import { Check, X, Mail, CalendarOff, Plus, Trash2, CarFront, Receipt, FileText, ClipboardList } from 'lucide-react';
import { db, sendGraceHavenEmail } from '@/lib/firebase';
import { useBusiness } from '@/lib/business';
import { useAuth, can } from '@/lib/auth';
import { useFleet } from '@/lib/useFleet';
import { formatPula, round2 } from '@/lib/money';
import { nextNumber } from '@/lib/numbering';
import { Modal } from '@/components/ui/Modal';
import type { CarHireRequest, CarHireStatus, BlockedRange, DocLineItem } from '@/lib/types';

const uid = () => Math.random().toString(36).slice(2, 9);
const STATUS: Record<CarHireStatus, string> = {
  new: 'bg-gold/15 text-gold-deep',
  confirmed: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-espresso/10 text-espresso',
  cancelled: 'bg-blush-soft text-blush-deep',
};

const KIND_LABEL: Record<'invoice' | 'receipt' | 'quotation', string> = {
  invoice: 'Invoice',
  receipt: 'Receipt',
  quotation: 'Quotation',
};

export function CarHire() {
  const biz = useBusiness();
  const { role } = useAuth();
  const { active: fleet } = useFleet();
  const canFinance = can(role, 'invoices');
  const [hires, setHires] = useState<CarHireRequest[]>([]);
  const [ranges, setRanges] = useState<BlockedRange[]>([]);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [notice, setNotice] = useState<{ title: string; body: string } | null>(null);
  // blocked-range form
  const [bCar, setBCar] = useState('');
  const [bFrom, setBFrom] = useState('');
  const [bTo, setBTo] = useState('');
  const [bNote, setBNote] = useState('');

  useEffect(() => {
    const q = query(collection(db, 'carHireRequests'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => setHires(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CarHireRequest, 'id'>) }))), () => setHires([]));
    const unsub2 = onSnapshot(doc(db, 'availability', 'carCalendar'), (snap) => setRanges(snap.exists() ? ((snap.data().ranges as BlockedRange[]) || []) : []), () => setRanges([]));
    return () => { unsub(); unsub2(); };
  }, []);

  function flash(m: string) { setToast(m); setTimeout(() => setToast(''), 3500); }

  async function setStatus(h: CarHireRequest, status: CarHireStatus) {
    try {
      await updateDoc(doc(db, 'carHireRequests', h.id), {
        status, updatedAt: new Date().toISOString(),
        ...(status === 'confirmed' ? { confirmedAt: new Date().toISOString() } : {}),
      });
      flash(`Marked ${status}.`);
    } catch (e) { flash(e instanceof Error ? e.message : 'Failed.'); }
  }

  async function confirmAndEmail(h: CarHireRequest) {
    setBusy(true);
    try {
      await updateDoc(doc(db, 'carHireRequests', h.id), {
        status: 'confirmed', confirmedAt: new Date().toISOString(),
        confirmationEmailSentAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
      });
      const body =
        `Dear ${h.fullName.split(' ')[0]},\n\n` +
        `Good news — your car hire with ${biz.name} is confirmed.\n\n` +
        `${h.vehicleName ? `Vehicle: ${h.vehicleName}\n` : ''}Pick-up: ${h.pickup}\nReturn: ${h.dropoff}` +
        `${h.days ? `\nDays: ${h.days}` : ''}${h.totalPrice ? `\nTotal: ${formatPula(h.totalPrice)}` : ''}\n\n` +
        `Please bring a valid driver's licence and ID at collection. To make any changes, reply to this email or call ${biz.phone}.`;
      await sendGraceHavenEmail({ to: h.email, subject: `Your car hire is confirmed — ${biz.name}`, body, relatedEntityType: 'carHire', relatedEntityId: h.id });
      flash('Confirmed and emailed the customer.');
    } catch (e) { flash(e instanceof Error ? e.message : 'Could not send.'); } finally { setBusy(false); }
  }

  /** Turn a hire into a finance document (receipt / invoice / quotation). */
  async function makeFinanceDoc(h: CarHireRequest, kind: 'invoice' | 'receipt' | 'quotation') {
    setBusy(true);
    try {
      const now = new Date().toISOString();
      const days = Math.max(1, Number(h.days || 1));
      const number = await nextNumber(kind);
      const unit = h.pricePerDay ? round2(Number(h.pricePerDay) * days) : Number(h.totalPrice || 0);
      const item: DocLineItem = {
        description: `${h.vehicleName || 'Vehicle'} hire — ${days} day${days === 1 ? '' : 's'}`,
        qty: 1,
        unitPrice: unit,
      };
      const total = round2(item.qty * item.unitPrice);
      const payload: Record<string, unknown> = {
        number, kind,
        clientName: h.fullName,
        items: [item],
        subtotal: total, vatRate: 0, vat: 0, total,
        status: kind === 'receipt' ? 'paid' : 'draft',
        createdAt: now, updatedAt: now,
      };
      if (h.email) payload.clientEmail = h.email;
      await addDoc(collection(db, 'documents'), payload);
      const label = KIND_LABEL[kind];
      setNotice({
        title: `${label} ${number} created`,
        body: `${label} ${number} created — find it in Invoices & Receipts to email or download.`,
      });
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Could not create the document.');
    } finally {
      setBusy(false);
    }
  }

  async function saveRanges(next: BlockedRange[]) {
    await setDoc(doc(db, 'availability', 'carCalendar'), { ranges: next, updatedAt: new Date().toISOString() }, { merge: true });
  }
  async function addRange() {
    if (!bFrom || !bTo) return flash('Pick both dates.');
    if (bTo < bFrom) return flash('End date must be after start.');
    const car = fleet.find((v) => v.id === bCar);
    // Firestore rejects `undefined` (especially inside an array) — only set present keys.
    const range: BlockedRange = { id: uid(), from: bFrom, to: bTo };
    if (bCar) range.carId = bCar;
    if (car?.name) range.carName = car.name;
    const note = bNote.trim();
    if (note) range.note = note;
    const next = [...ranges, range];
    try { await saveRanges(next); setBFrom(''); setBTo(''); setBNote(''); setBCar(''); flash('Dates blocked.'); }
    catch (e) { flash(e instanceof Error ? e.message : 'Failed.'); }
  }
  async function removeRange(id: string) {
    try { await saveRanges(ranges.filter((r) => r.id !== id)); flash('Removed.'); }
    catch (e) { flash(e instanceof Error ? e.message : 'Failed.'); }
  }

  const sortedRanges = useMemo(() => [...ranges].sort((a, b) => a.from.localeCompare(b.from)), [ranges]);

  return (
    <div>
      <h1 className="font-display text-3xl text-espresso">Car Hire &amp; Availability</h1>
      <p className="mt-1 font-body text-sm text-muted">Confirm hire requests and block out unavailable dates.</p>

      {/* Requests */}
      <p className="eyebrow mt-8 mb-3">Hire requests</p>
      <div className="overflow-x-auto border border-line bg-paper">
        <table className="w-full min-w-[680px] text-left">
          <thead>
            <tr className="border-b border-line font-body text-[11px] uppercase tracking-wide text-muted">
              <th className="px-5 py-3">Customer</th><th className="px-5 py-3">Vehicle</th><th className="px-5 py-3">Dates</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {hires.map((h) => (
              <tr key={h.id} className="border-b border-line last:border-0 hover:bg-cream/40">
                <td className="px-5 py-3.5"><p className="font-body text-sm font-semibold text-espresso">{h.fullName}</p><p className="font-body text-xs text-muted">{h.email}</p></td>
                <td className="px-5 py-3.5 font-body text-sm text-muted">{h.vehicleName || 'Any'}</td>
                <td className="px-5 py-3.5 font-body text-sm text-muted">{h.pickup} → {h.dropoff}{h.days ? ` · ${h.days}d` : ''}{h.totalPrice ? ` · ${formatPula(h.totalPrice)}` : ''}</td>
                <td className="px-5 py-3.5"><span className={`inline-block rounded-none px-2.5 py-1 font-body text-[10px] font-bold uppercase tracking-wide ${STATUS[h.status]}`}>{h.status}</span></td>
                <td className="px-5 py-3.5">
                  <div className="flex items-center justify-end gap-3 text-muted">
                    <button onClick={() => confirmAndEmail(h)} disabled={busy} title="Confirm & email" className="hover:text-emerald-600"><Mail size={16} /></button>
                    <button onClick={() => setStatus(h, 'completed')} title="Completed" className="hover:text-espresso"><Check size={16} /></button>
                    <button onClick={() => setStatus(h, 'cancelled')} title="Cancel" className="hover:text-blush-deep"><X size={16} /></button>
                    {canFinance && (
                      <>
                        <span className="h-4 w-px bg-line" aria-hidden />
                        <button onClick={() => makeFinanceDoc(h, 'receipt')} disabled={busy} title="Create receipt" className="hover:text-gold-deep disabled:opacity-30"><Receipt size={16} /></button>
                        <button onClick={() => makeFinanceDoc(h, 'invoice')} disabled={busy} title="Create invoice" className="hover:text-gold-deep disabled:opacity-30"><FileText size={16} /></button>
                        <button onClick={() => makeFinanceDoc(h, 'quotation')} disabled={busy} title="Create quotation" className="hover:text-gold-deep disabled:opacity-30"><ClipboardList size={16} /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {hires.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center font-body text-sm text-muted">No hire requests yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Blocked dates */}
      <p className="eyebrow mt-10 mb-3 flex items-center gap-2"><CalendarOff size={15} /> Block out dates</p>
      <div className="border border-line bg-paper p-6">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto_1fr_auto]">
          <select className="field" value={bCar} onChange={(e) => setBCar(e.target.value)}>
            <option value="">All vehicles</option>
            {fleet.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <input type="date" className="field" value={bFrom} onChange={(e) => setBFrom(e.target.value)} />
          <input type="date" className="field" value={bTo} onChange={(e) => setBTo(e.target.value)} />
          <input className="field" placeholder="Note (optional)" value={bNote} onChange={(e) => setBNote(e.target.value)} />
          <button onClick={addRange} className="btn-primary !px-4 !py-2.5"><Plus size={15} /> Block</button>
        </div>

        <div className="mt-5 divide-y divide-line border-t border-line">
          {sortedRanges.map((r) => (
            <div key={r.id} className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3 font-body text-sm">
                <CarFront size={15} className="text-gold-deep" />
                <span className="text-espresso">{r.from} → {r.to}</span>
                <span className="text-muted">· {r.carName || 'All vehicles'}{r.note ? ` · ${r.note}` : ''}</span>
              </div>
              <button onClick={() => removeRange(r.id)} className="text-muted hover:text-blush-deep" aria-label="Remove"><Trash2 size={15} /></button>
            </div>
          ))}
          {sortedRanges.length === 0 && <p className="py-4 font-body text-sm text-muted">No blocked dates. The website shows all dates as available.</p>}
        </div>
      </div>

      {notice && (
        <Modal onClose={() => setNotice(null)}>
          <h2 className="font-display text-2xl text-espresso">{notice.title}</h2>
          <div className="rule-gold mt-3" />
          <p className="mt-4 font-body text-sm leading-relaxed text-muted">{notice.body}</p>
          <div className="mt-6 flex justify-end">
            <button onClick={() => setNotice(null)} className="btn-primary">Done</button>
          </div>
        </Modal>
      )}

      {toast && <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 bg-espresso px-5 py-3 font-body text-sm text-cream shadow-soft">{toast}</div>}
    </div>
  );
}
